import React, { useState, useEffect, useMemo } from "react";
import { useCustomerShortCodes } from "@/lib/utils/customer/useCustomerShortCodes";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { AlertTriangle, Check, CheckCircle2, ChevronsUpDown, Loader2, Package, PackageOpen } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useTrailerTypes } from "@/hooks/useTrailerTypes";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { DocumentUpload } from "@/components/documents/DocumentUpload";
import { YardPhotoCapture } from "@/components/yard/YardPhotoCapture";
import { TrailerIdCombobox } from "@/components/yard/TrailerIdCombobox";
import { NeedsServiceToggle } from "@/components/yard/NeedsServiceToggle";
import { isTrailerArrived, type CheckInMetadata } from "@/lib/trailer-yard-ops";
import {
  TrailerCrossTenantDialog,
  findTrailerInOtherTenants,
  type OtherTenantTrailerMatch,
} from "@/components/trailers/TrailerCrossTenantDialog";

/** What a parent-controlled CheckInForm exposes through `submitRef`. */
export interface CheckInFormHandle {
  submit: () => void;
  isReady: boolean;
  trailerId: string;
  /** Everything the form would submit (the same data manual check-in sends). */
  checkInMeta: () => CheckInMetadata;
}

interface CheckInFormProps {
  locationId: string;
  locationCity: string;
  locationName: string;
  isProcessing: boolean;
  pendingPhoto: File | null;
  onPhotoChange: (photo: File | null) => void;
  /** Right-side trailer photo (paired with `pendingPhoto` as the left side). */
  pendingPhotoRight?: File | null;
  onPhotoRightChange?: (photo: File | null) => void;
  pendingSealPhoto: File | null;
  onSealPhotoChange: (photo: File | null) => void;
  pendingLicensePlatePhoto: File | null;
  onLicensePlatePhotoChange: (photo: File | null) => void;
  pendingBolPhoto: File | null;
  onBolPhotoChange: (photo: File | null) => void;
  processManualAction: (trailerId: string, meta?: CheckInMetadata) => Promise<boolean>;
  createAndProcess: (trailerId: string, trailerTypeId?: string | null, meta?: CheckInMetadata) => Promise<void>;
  /** When provided, the internal submit button is hidden and the parent controls submission */
  submitRef?: React.MutableRefObject<CheckInFormHandle | null>;
  onReadyChange?: (ready: boolean) => void;
  onSuccess?: () => void;
  hideTitle?: boolean;
  hideTruckId?: boolean;
  /** Mark check-in as "found during yard audit" */
  isFoundTrailer?: boolean;
  /** When provided, the Trailer ID is pre-filled and cannot be changed */
  lockedTrailerId?: string;
}

type ScacOption = { code: string; name: string };

function ScacCombobox({ value, onChange, options }: { value: string; onChange: (val: string) => void; options: ScacOption[] }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? options.filter((o) =>
        o.code.toLowerCase().includes(search.toLowerCase()) ||
        o.name.toLowerCase().includes(search.toLowerCase())
      )
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-mono">
          {value || <span className="text-muted-foreground font-sans">e.g. JBHT, SNLU...</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search SCAC..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {filtered.length > 0 && (
              <CommandGroup heading="Suggestions">
                {filtered.map((opt) => (
                  <CommandItem
                    key={opt.code}
                    value={opt.code}
                    onSelect={() => { onChange(opt.code); setSearch(""); setOpen(false); }}
                  >
                    <span className="font-mono">{opt.code}</span>
                    <span className="text-muted-foreground">: {opt.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && (
              <CommandEmpty>No matching SCAC codes.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TruckIdCombobox({ value, onChange, tractors, disabled, strict }: { value: string; onChange: (val: string) => void; tractors: { id: string; name: string }[]; disabled?: boolean; strict?: boolean }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = search
    ? tractors.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()))
    : tractors;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} disabled={disabled} className="w-full justify-between">
          {value || <span className="text-muted-foreground">{strict ? "Select truck..." : "Select or enter truck ID..."}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type or search truck..." value={search} onValueChange={setSearch} />
          <CommandList>
            {!strict && search && !tractors.some((t) => t.name.toLowerCase() === search.toLowerCase()) && (
              <CommandItem value={search} onSelect={() => { onChange(search); setSearch(""); setOpen(false); }}>
                Use "{search}"
              </CommandItem>
            )}
            {filtered.length > 0 && (
              <CommandGroup heading="Tractors">
                {filtered.map((t) => (
                  <CommandItem key={t.id} value={t.name} onSelect={() => { onChange(t.name); setSearch(""); setOpen(false); }}>
                    <Check className={`mr-2 h-4 w-4 ${value === t.name ? "opacity-100" : "opacity-0"}`} />
                    {t.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {filtered.length === 0 && (
              <CommandEmpty>No matching tractors.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function CheckInForm({
  locationId,
  locationCity,
  locationName,
  isProcessing,
  pendingPhoto,
  onPhotoChange,
  pendingPhotoRight,
  onPhotoRightChange,
  pendingSealPhoto,
  onSealPhotoChange,
  pendingLicensePlatePhoto,
  onLicensePlatePhotoChange,
  pendingBolPhoto,
  onBolPhotoChange,
  processManualAction,
  createAndProcess,
  submitRef,
  onReadyChange,
  onSuccess,
  hideTitle,
  hideTruckId,
  isFoundTrailer,
  lockedTrailerId,
}: CheckInFormProps) {
  const [trailerId, setTrailerId] = useState(lockedTrailerId ?? "");
  const [truckId, setTruckId] = useState("");
  const [truckOwner, setTruckOwner] = useState<"aurora" | "partner" | "">("");
  const [trailerTypeId, setTrailerTypeId] = useState("");
  const [loadStatus, setLoadStatus] = useState<"empty" | "loaded" | "">("");
  const [sealId, setSealId] = useState("");
  const [sealStatus, setSealStatus] = useState<"intact" | "broken" | "">("");
  const [scac, setScac] = useState("");
  const [isCustomerParking, setIsCustomerParking] = useState(false);
  const [needsService, setNeedsService] = useState(false);
  const [needsServiceReason, setNeedsServiceReason] = useState("");
  const [isKnownTrailer, setIsKnownTrailer] = useState<boolean | null>(null);
  // True only when the matched existing trailer already has a type on record.
  // A known trailer with no type must stay editable so staff can fill it in.
  const [knownTrailerHasType, setKnownTrailerHasType] = useState(false);
  const [showNewTrailerConfirm, setShowNewTrailerConfirm] = useState(false);
  // EAPI reported this trailer arrived and it is pending check-in: the
  // operator must confirm they inspected it before checking it in.
  const [isArrivedTrailer, setIsArrivedTrailer] = useState(false);
  const [inspectionConfirmed, setInspectionConfirmed] = useState(false);
  const [crossTenantMatches, setCrossTenantMatches] = useState<OtherTenantTrailerMatch[]>([]);
  const [showCrossTenantDialog, setShowCrossTenantDialog] = useState(false);

  const { data: trailerTypes } = useTrailerTypes();
  const { tenantId } = useTenant();

  // Customer short codes for SCAC suggestions (shared with driver Verify Trailer)
  const { data: scacRows = [] } = useCustomerShortCodes();
  const customerShortCodes = useMemo(
    () => scacRows.map((c) => ({ code: c.short_code, name: c.organization_name ?? "" })),
    [scacRows],
  );

  // Look up known trailers for autocomplete
  const { data: knownTrailers = [] } = useQuery({
    queryKey: ["trailers-lookup", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from("trailers")
        .select("id, trailer_id, trailer_type_id, scac, seal_status, current_location_status")
        .eq("tenant_id", tenantId!)
        .order("trailer_id");
      return data || [];
    },
  });

  // Look up tractors for truck ID dropdown
  const { data: tractors = [] } = useQuery({
    queryKey: ["tractors-lookup"],
    queryFn: async () => {
      const { data } = await supabase
        .from("tractors")
        .select("id, name")
        .order("name");
      return data || [];
    },
  });

  // Check if trailer ID matches a known trailer
  useEffect(() => {
    const trimmed = trailerId.trim().toUpperCase();
    if (!trimmed) {
      setIsKnownTrailer(null);
      setKnownTrailerHasType(false);
      setIsArrivedTrailer(false);
      return;
    }
    const match = knownTrailers.find(
      (t) => t.trailer_id.toUpperCase() === trimmed
    );
    setIsArrivedTrailer(isTrailerArrived(match));
    if (match) {
      setIsKnownTrailer(true);
      setKnownTrailerHasType(!!match.trailer_type_id);
      if (match.trailer_type_id) setTrailerTypeId(match.trailer_type_id);
      if (match.scac) setScac(match.scac);
      if ((match as any).seal_status === "intact" || (match as any).seal_status === "broken") {
        setSealStatus((match as any).seal_status);
      }
    } else {
      setIsKnownTrailer(false);
      setKnownTrailerHasType(false);
    }
  }, [trailerId, knownTrailers]);

  const buildMeta = (): CheckInMetadata => ({
    truckId: truckId.trim() || undefined,
    truckOwner: truckOwner || undefined,
    sealId: sealId.trim() || undefined,
    loadStatus: loadStatus as "empty" | "loaded",
    scac: scac.trim() || undefined,
    isCustomerParking,
    isFoundTrailer,
    sealStatus: loadStatus === "loaded" && sealStatus !== "" ? sealStatus : undefined,
    needsService,
    needsServiceReason: needsService ? needsServiceReason.trim() || undefined : undefined,
    trailerTypeId: trailerTypeId || undefined,
    inspected: isArrivedTrailer && inspectionConfirmed ? true : undefined,
  });

  const resetForm = () => {
    setTrailerId("");
    setTruckId("");
    setTruckOwner("");
    setTrailerTypeId("");
    setLoadStatus("");
    setSealId("");
    setSealStatus("");
    setScac("");
    setIsCustomerParking(false);
    setNeedsService(false);
    setNeedsServiceReason("");
    setIsKnownTrailer(null);
    setKnownTrailerHasType(false);
    setIsArrivedTrailer(false);
    setInspectionConfirmed(false);
    onPhotoChange(null);
    onSealPhotoChange(null);
    onLicensePlatePhotoChange(null);
    onBolPhotoChange(null);
  };

  const handleSubmit = async () => {
    const trimmed = trailerId.trim();
    if (!trimmed) return;

    const found = await processManualAction(trimmed, buildMeta());
    if (found) {
      resetForm();
      onSuccess?.();
    } else {
      // Trailer not found locally — see if it lives in another tenant before creating a new one.
      if (tenantId) {
        const matches = await findTrailerInOtherTenants(trimmed, tenantId);
        if (matches.length > 0) {
          setCrossTenantMatches(matches);
          setShowCrossTenantDialog(true);
          return;
        }
      }
      setShowNewTrailerConfirm(true);
    }
  };

  const handleCreateAndCheckIn = async () => {
    const trimmed = trailerId.trim();
    await createAndProcess(trimmed, trailerTypeId, buildMeta());
    resetForm();
    setShowNewTrailerConfirm(false);
    onSuccess?.();
  };
  const isReady =
    trailerId.trim().length > 0 &&
    (hideTruckId || (truckId.trim().length > 0 && truckOwner !== "")) &&
    trailerTypeId !== "" &&
    scac.trim().length > 0 &&
    loadStatus !== "" &&
    (loadStatus !== "loaded" || (sealId.trim().length > 0 && sealStatus !== "")) &&
    !!pendingPhoto &&
    !!pendingPhotoRight &&
    !!pendingLicensePlatePhoto &&
    // Seal photo is only required for loaded trailers — empty trailers have no seal.
    (loadStatus !== "loaded" || !!pendingSealPhoto) &&
    (loadStatus === "empty" || !!pendingBolPhoto) &&
    (!needsService || needsServiceReason.trim().length > 0) &&
    (!isArrivedTrailer || inspectionConfirmed);

  // Expose submit to parent when submitRef is provided
  React.useEffect(() => {
    if (submitRef) {
      submitRef.current = {
        submit: handleSubmit,
        isReady,
        trailerId: trailerId.trim(),
        checkInMeta: buildMeta,
      };
    }
    onReadyChange?.(isReady);
    return () => { if (submitRef) submitRef.current = null; };
  });

  return (
    <>
      <div className="space-y-4">
        {!hideTitle && <h3 className="text-sm font-medium">Manual Trailer Check In</h3>}

        {/* Trailer ID */}
        <div className="space-y-1.5">
          <Label className="text-xs">Trailer ID *</Label>
          {lockedTrailerId ? (
            <Input value={trailerId} readOnly disabled className="font-mono" />
          ) : (
            <TrailerIdCombobox
              value={trailerId}
              onChange={(val) => setTrailerId(val.toUpperCase())}
              trailers={knownTrailers}
            />
          )}
          {isKnownTrailer === true && (
            <p className="text-xs text-primary">✓ Known AURY trailer</p>
          )}
          {isKnownTrailer === false && trailerId.trim() && (
            <p className="text-xs text-muted-foreground">
              This trailer is not in our system. The new trailer will be created once the check-in is complete.
            </p>
          )}
        </div>

        {/* Truck ID */}
        {!hideTruckId && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Truck Owner *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={truckOwner === "aurora" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (truckOwner !== "aurora") setTruckId("");
                    setTruckOwner("aurora");
                  }}
                >
                  Aurora
                </Button>
                <Button
                  type="button"
                  variant={truckOwner === "partner" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (truckOwner !== "partner") setTruckId("");
                    setTruckOwner("partner");
                  }}
                >
                  Partner
                </Button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Truck ID *</Label>
              {truckOwner === "partner" ? (
                <Input
                  placeholder="Enter truck ID..."
                  value={truckId}
                  onChange={(e) => setTruckId(e.target.value)}
                  disabled={!truckOwner}
                />
              ) : (
                <TruckIdCombobox
                  value={truckId}
                  onChange={setTruckId}
                  tractors={tractors}
                  disabled={!truckOwner}
                  strict
                />
              )}
            </div>
          </>
        )}

        {/* Trailer Type */}
        <div className="space-y-1.5">
          <Label className="text-xs">Trailer Type *</Label>
          <Select
            value={trailerTypeId}
            onValueChange={setTrailerTypeId}
            disabled={isKnownTrailer === true && knownTrailerHasType}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select trailer type" />
            </SelectTrigger>
            <SelectContent>
              {trailerTypes?.map((tt) => (
                <SelectItem key={tt.id} value={tt.id}>{tt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isKnownTrailer === true && knownTrailerHasType && (
            <p className="text-xs text-muted-foreground">Trailer type set from existing trailer record</p>
          )}
          {isKnownTrailer === true && !knownTrailerHasType && (
            <p className="text-xs text-muted-foreground">
              This trailer has no type on record — select one and it will be saved with this check-in.
            </p>
          )}
        </div>

        {/* SCAC */}
        <div className="space-y-1.5">
          <Label className="text-xs">SCAC *</Label>
          <ScacCombobox
            value={scac}
            onChange={(val) => setScac(val.toUpperCase())}
            options={customerShortCodes}
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Load Status *</Label>
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={loadStatus === "empty" ? "default" : "outline"}
              size="sm"
              onClick={() => setLoadStatus("empty")}
            >
              <PackageOpen className="h-4 w-4 mr-2" />
              Empty
            </Button>
            <Button
              type="button"
              variant={loadStatus === "loaded" ? "default" : "outline"}
              size="sm"
              onClick={() => setLoadStatus("loaded")}
            >
              <Package className="h-4 w-4 mr-2" />
              Loaded
            </Button>
          </div>
        </div>

        {/* Seal ID — visible when loaded */}
        {loadStatus === "loaded" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Seal ID *</Label>
              <Input
                placeholder="Enter seal number..."
                value={sealId}
                onChange={(e) => setSealId(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Seal Status *</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={sealStatus === "intact" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSealStatus("intact")}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Intact
                </Button>
                <Button
                  type="button"
                  variant={sealStatus === "broken" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSealStatus("broken")}
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Broken
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Customer Parking */}
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div>
            <Label className="text-sm font-medium">Customer Parking</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Trailer parked by a customer, not fleet-managed</p>
          </div>
          <Switch checked={isCustomerParking} onCheckedChange={setIsCustomerParking} />
        </div>

        {/* Needs Service */}
        <NeedsServiceToggle
          needsService={needsService}
          needsServiceReason={needsServiceReason}
          onNeedsServiceChange={setNeedsService}
          onReasonChange={setNeedsServiceReason}
        />

        {/* Trailer Photo */}
        <YardPhotoCapture
          photo={pendingPhoto}
          onPhotoChange={onPhotoChange}
          label="Trailer Photo (Left side) *"
          captureLabel={pendingPhoto ? "Retake Left Side Photo" : "Take Left Side Photo"}
        />
        <YardPhotoCapture
          photo={pendingPhotoRight ?? null}
          onPhotoChange={(f) => onPhotoRightChange?.(f)}
          label="Trailer Photo (Right side) *"
          captureLabel={pendingPhotoRight ? "Retake Right Side Photo" : "Take Right Side Photo"}
        />

        {/* License Plate Photo — always required */}
        <YardPhotoCapture
          photo={pendingLicensePlatePhoto}
          onPhotoChange={onLicensePlatePhotoChange}
          label="License Plate Photo *"
          captureLabel={pendingLicensePlatePhoto ? "Retake License Plate Photo" : "Take License Plate Photo"}
        />

        {/* Seal Photo — required only when the trailer is loaded */}
        {loadStatus === "loaded" && (
          <YardPhotoCapture
            photo={pendingSealPhoto}
            onPhotoChange={onSealPhotoChange}
            label="Seal Photo *"
            captureLabel={pendingSealPhoto ? "Retake Seal Photo" : "Take Seal Photo"}
          />
        )}

        {/* Bill of Lading Photo — required only when the trailer is loaded.
            Empty trailers (deadhead / reposition) carry no BoL. */}
        <YardPhotoCapture
          photo={pendingBolPhoto}
          onPhotoChange={onBolPhotoChange}
          label={loadStatus === "empty" ? "Bill of Lading" : "Bill of Lading *"}
          icon="document"
          captureLabel={pendingBolPhoto ? "Retake BOL Photo" : "BOL Photo"}
        />

        {/* Arrived trailers (reported by EAPI, pending check-in) must be inspected first. */}
        {isArrivedTrailer && (
          <div className="flex items-start gap-3 rounded-lg border p-3">
            <Checkbox
              id="trailer-inspected"
              checked={inspectionConfirmed}
              onCheckedChange={(v) => setInspectionConfirmed(v === true)}
            />
            <div>
              <Label htmlFor="trailer-inspected" className="text-sm font-medium">
                I have inspected the trailer *
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Required to check in a trailer reported as arrived.
              </p>
            </div>
          </div>
        )}

        {/* Submit — only shown when parent doesn't control it */}
        {!submitRef && !isFoundTrailer && (
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!isReady || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              "Check In"
            )}
          </Button>
        )}
      </div>

      {/* Fixed bottom action bar for found trailer */}
      {!submitRef && isFoundTrailer && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t z-50">
          <Button
            className="w-full"
            size="lg"
            onClick={handleSubmit}
            disabled={!isReady || isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              "Check in found trailer"
            )}
          </Button>
        </div>
      )}

      {/* New trailer confirmation dialog */}
      <AlertDialog open={showNewTrailerConfirm} onOpenChange={setShowNewTrailerConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Trailer Not Found
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  <strong className="text-foreground">{trailerId.trim()}</strong> does not
                  exist in the system. A new trailer record will be created and checked in at{" "}
                  <strong className="text-foreground">{locationName}</strong>.
                </p>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1.5">Trailer Type</label>
                  <Select value={trailerTypeId} onValueChange={setTrailerTypeId}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {trailerTypes?.map((tt) => (
                        <SelectItem key={tt.id} value={tt.id}>{tt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCreateAndCheckIn(); }}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Create & Check In"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cross-tenant trailer migration dialog */}
      <TrailerCrossTenantDialog
        open={showCrossTenantDialog}
        onOpenChange={setShowCrossTenantDialog}
        trailerIdText={trailerId.trim()}
        matches={crossTenantMatches}
        destinationLocationId={locationId}
        destinationStatus="at_site"
        onMigrated={async () => {
          // Re-run the original check-in path now that the trailer exists locally.
          const found = await processManualAction(trailerId.trim(), buildMeta());
          if (found) {
            resetForm();
            onSuccess?.();
          }
        }}
        onCreateSeparate={() => setShowNewTrailerConfirm(true)}
      />
    </>
  );
}
